import { useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/library/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NormalSelector from "@/components/ui/others/NormalSelector";
import { X, Flag, AlertTriangle } from "lucide-react";
import { toPaise, fromPaise } from "./installmentMath";
import type { Installment } from "./types";
import { useForecloseInstallmentPlan } from "@/features/leads/api/useInstallmentMutations";

/**
 * Foreclose — settle the whole remaining schedule in one go.
 *
 * The dialog's job is to make the arithmetic undeniable BEFORE anyone commits:
 * what is still owed, how much of it the customer has already handed over as
 * unadjusted credit, and therefore what actually has to be collected today.
 * Getting that split wrong means either collecting the same money twice or
 * closing a plan that was never paid.
 */

const METHODS = ["UPI", "Bank Transfer", "Cash", "Card", "Cheque"];

function money(n: number): string {
  const hasPaise = Math.round(n * 100) % 100 !== 0;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  childId: number;
  dealName: string;
  installments: Installment[];
  /** Cash on the deal that no instalment claims yet. */
  unadjusted: number;
  /** The deal's own remaining balance — the ceiling on what can be collected. */
  dealPending: number;
  /** Opens the plan editor — the way out when the schedule outgrew the deal. */
  onRebuild?: () => void;
}

export default function ForecloseDialog({
  open,
  onClose,
  childId,
  dealName,
  installments,
  unadjusted,
  dealPending,
  onRebuild,
}: Props) {
  const [useCredit, setUseCredit] = useState(true);
  const [method, setMethod] = useState("UPI");
  const [txnId, setTxnId] = useState("");
  const foreclose = useForecloseInstallmentPlan();

  const quote = useMemo(() => {
    const unpaid = installments.filter(
      (i) => i.status !== "PAID" && i.status !== "WAIVED",
    );
    // Paise throughout — this is the number someone is about to be charged.
    const outstandingP = unpaid.reduce(
      (s, i) =>
        s + (toPaise(i.amount) - toPaise(i.paidAmount) - toPaise(i.waivedAmount)),
      0,
    );
    const creditP = useCredit ? Math.min(toPaise(unadjusted), outstandingP) : 0;
    const cashP = Math.max(0, outstandingP - creditP);
    return {
      count: unpaid.length,
      outstanding: fromPaise(outstandingP),
      credit: fromPaise(creditP),
      cash: fromPaise(cashP),
      exceedsDeal: cashP > toPaise(dealPending),
    };
  }, [installments, unadjusted, useCredit, dealPending]);

  const submit = async () => {
    try {
      await foreclose.mutateAsync({
        childId,
        method,
        txnId: txnId || null,
        useCredit,
        // The server re-derives this and refuses if it has moved since the
        // dialog was drawn — another counsellor recording a payment mid-flight
        // must not silently change what gets charged.
        expectedAmount: quote.cash,
      });
      onClose();
    } catch {
      /* the hook toasts; keep the dialog open */
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[420px] rounded-[20px] p-0 gap-0 [&>button]:hidden">
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-[#e6e8e7]">
          <div className="flex items-center gap-2.5">
            <div className="size-8 rounded-xl bg-[#eaf5f1] flex items-center justify-center">
              <Flag className="size-4 text-[#218760]" />
            </div>
            <div>
              <p className="text-[13px] font-medium text-[#1e302a]">
                Foreclose instalment plan
              </p>
              <p className="text-[10px] text-[#9ba3a0]">{dealName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="size-7 rounded-full flex items-center justify-center hover:bg-[#f7f9f8]"
            aria-label="Close"
          >
            <X className="size-3.5 text-[#697571]" />
          </button>
        </div>

        <div className="px-4 py-3 flex flex-col gap-3">
          {/* The arithmetic, stated plainly. */}
          <div className="rounded-xl border border-[#e6e8e7] overflow-hidden text-[12px]">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8]">
              <span className="text-[#697571]">
                {quote.count} instalment{quote.count === 1 ? "" : "s"} still open
              </span>
              <span className="tabular-nums font-medium text-[#1e302a]">
                {money(quote.outstanding)}
              </span>
            </div>
            {unadjusted > 0 && (
              <label className="flex items-center justify-between px-3 py-2 border-t border-[#eef1f0] cursor-pointer">
                <span className="flex items-center gap-2 text-[#374741]">
                  <input
                    type="checkbox"
                    checked={useCredit}
                    onChange={(e) => setUseCredit(e.target.checked)}
                    className="size-3.5 accent-[#218760]"
                  />
                  Use unadjusted credit
                </span>
                <span className="tabular-nums text-[#156548]">
                  − {money(quote.credit)}
                </span>
              </label>
            )}
            <div className="flex items-center justify-between px-3 py-2 border-t border-[#eef1f0] bg-white">
              <span className="font-medium text-[#1e302a]">Collect now</span>
              <span className="tabular-nums font-semibold text-[16px] text-[#1e302a]">
                {money(quote.cash)}
              </span>
            </div>
          </div>

          {quote.cash === 0 && (
            <p className="text-[10px] text-[#156548] leading-[1.5]">
              The credit already on this deal covers the whole balance — nothing
              more needs collecting. Foreclosing just applies it and closes the
              schedule.
            </p>
          )}

          {quote.exceedsDeal && (
            // A disabled button with no explanation is a dead end. Say what is
            // wrong, and hand over the action that fixes it.
            <div className="rounded-lg bg-[#fff5f6] border border-[#f3c9d0] px-2.5 py-2">
              <div className="flex items-start gap-1.5">
                <AlertTriangle className="size-3.5 text-[#da2a46] mt-[1px] shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-[#a01b32]">
                    Can't foreclose — the schedule is bigger than the deal
                  </p>
                  <p className="text-[10px] text-[#a01b32]/90 leading-[1.45] mt-0.5">
                    Closing it needs {money(quote.cash)}, but the deal only has{" "}
                    {money(dealPending)} left to collect. The plan was built for
                    more than the deal is worth. Change the plan to the deal's
                    real value, then foreclose.
                  </p>
                  {onRebuild && (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onRebuild();
                      }}
                      className="mt-1.5 inline-flex items-center rounded-lg bg-[#a01b32] px-2.5 py-1 text-[10px] font-medium text-white hover:bg-[#87172a] transition-colors"
                    >
                      Change the plan
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {quote.cash > 0 && !quote.exceedsDeal && (
            <div className="grid grid-cols-2 gap-2.5">
              <NormalSelector
                title="Method"
                value={method}
                onChange={(v: string) => setMethod(v)}
                options={METHODS.map((m) => ({ value: m, name: m }))}
              />
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-[#374741]">
                  Reference
                </label>
                <Input
                  value={txnId}
                  onChange={(e) => setTxnId(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
          )}

          <p className="text-[10px] text-[#9ba3a0] leading-[1.5]">
            Every remaining instalment is marked paid and the schedule closes, so
            their due dates stop generating reminders. No early-settlement
            discount is applied — take that off the deal's discount, or waive
            instalments first, then foreclose the remainder.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#e6e8e7]">
          <Button variant="cancel" size="sm" onClick={onClose} disabled={foreclose.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={foreclose.isPending || quote.count === 0 || quote.exceedsDeal}
          >
            {foreclose.isPending
              ? "Foreclosing…"
              : quote.cash > 0
                ? `Collect ${money(quote.cash)} & close`
                : "Apply credit & close"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
